import { useEffect, useState } from "react";
import { CalendarIcon } from "lucide-react";
import { format, subDays } from "date-fns";
import { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";

export interface DateRangePickerProps {
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange | undefined) => void;
  className?: string;
}

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [internalRange, setInternalRange] = useState<DateRange | undefined>(
    dateRange ?? {
      from: subDays(new Date(), 6),
      to: new Date(),
    }
  );

  // Keep internal state in sync with external prop
  useEffect(() => {
    if (dateRange) {
      setInternalRange(dateRange);
    }
  }, [dateRange?.from, dateRange?.to]);

  const handleSelect = (range: DateRange | undefined) => {
    setInternalRange(range);
    onDateRangeChange?.(range);
  };

  const label = (() => {
    if (!internalRange?.from) return "Select date range";
    if (!internalRange.to) return format(internalRange.from, "MMM dd, yyyy");
    return `${format(internalRange.from, "MMM dd, yyyy")} - ${format(
      internalRange.to,
      "MMM dd, yyyy"
    )}`;
  })();

  return (
    <div className={cn("relative inline-block", className)}>
      <Button
        id="date"
        variant="outline"
        size="sm"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          "w-[280px] justify-start text-left font-normal",
          !internalRange && "text-muted-foreground"
        )}
        data-testid="button-daterange-picker"
      >
        <CalendarIcon className="mr-2 h-4 w-4" />
        {label}
      </Button>

      {open && (
        <div className="absolute right-0 mt-2 z-50 rounded-md border bg-popover p-3 shadow-md">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={internalRange?.from ?? new Date()}
            selected={internalRange}
            onSelect={handleSelect}
            numberOfMonths={2}
            disabled={(date) => date > new Date()}
            className="rounded-md border"
            data-testid="calendar-custom-range"
          />
          <div className="mt-3 flex justify-end">
            <Button
              size="sm"
              onClick={() => setOpen(false)}
              disabled={!internalRange?.from || !internalRange?.to}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
